import {
  createField,
  deleteField,
  getDocuments,
  getFields,
  getParticipants,
  updateField,
  type CreateFieldData,
  type ParticipantInDb,
  type SignatureFieldInDb,
  type SignatureFieldUpdate,
  type VaultFileSigningRequestInDb,
} from '@/api';
import { extractErrorDetail } from '@/utils/errorHandler';

type SignatureFieldCreate = CreateFieldData['body'];

// NOTE: Coordinates (x, y, width, height) are stored as percentage values (0-100)
// relative to the rendered PDF page dimensions. Backend should confirm if a different
// unit (pixels, PDF pts) is expected before forwarding to the signing provider.

export async function fetchSignatureFields(
  participantUid: string,
  signingRequestUid: string
): Promise<SignatureFieldInDb[]> {
  const response = await getFields({
    path: {
      participant_uid: participantUid,
      signing_request_uid: signingRequestUid,
    },
  });

  if (response.error) {
    throw new Error(extractErrorDetail(response.error, 'Failed to load signature fields.'));
  }

  return response.data;
}

export async function createSignatureField(
  participantUid: string,
  signingRequestUid: string,
  payload: SignatureFieldCreate
): Promise<SignatureFieldInDb> {
  const response = await createField({
    path: {
      participant_uid: participantUid,
      signing_request_uid: signingRequestUid,
    },
    body: payload,
  });

  if (response.error) {
    throw new Error(extractErrorDetail(response.error, 'Failed to create signature field.'));
  }

  return response.data;
}

export async function updateSignatureField(
  fieldUid: string,
  participantUid: string,
  signingRequestUid: string,
  payload: SignatureFieldUpdate
): Promise<SignatureFieldInDb> {
  const response = await updateField({
    path: {
      signature_field_uid: fieldUid,
      signing_request_uid: signingRequestUid,
      participant_uid: participantUid,
    },
    body: payload,
  });

  if (response.error) {
    throw new Error(extractErrorDetail(response.error, 'Failed to update signature field.'));
  }

  return response.data;
}

export async function deleteSignatureField(
  fieldUid: string,
  participantUid: string,
  signingRequestUid: string
): Promise<SignatureFieldInDb> {
  const response = await deleteField({
    path: {
      signature_field_uid: fieldUid,
      signing_request_uid: signingRequestUid,
      participant_uid: participantUid,
    },
  });

  if (response.error) {
    throw new Error(extractErrorDetail(response.error, 'Failed to delete signature field.'));
  }

  return response.data;
}

export async function fetchParticipants(signingRequestUid: string): Promise<ParticipantInDb[]> {
  const response = await getParticipants({
    path: { signing_request_uid: signingRequestUid },
  });

  if (response.error) {
    throw new Error(extractErrorDetail(response.error, 'Failed to load signing participants.'));
  }

  return response.data;
}

export async function fetchSigningVaultFiles(
  signingRequestUid: string
): Promise<VaultFileSigningRequestInDb[]> {
  const response = await getDocuments({
    path: { signing_request_uid: signingRequestUid },
  });

  if (response.error) {
    throw new Error(extractErrorDetail(response.error, 'Failed to load signing documents.'));
  }

  return response.data;
}
